import * as React from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Card,
  ChannelIcon,
  Flex,
  NotifyMeIcon,
  NotifyMePublished,
  RepeatIcon,
  Tag,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'
import { getRecurrenceSummary } from '~publish/components/PostCard/PostCardActions/recurrenceHelpers'
import { PostMediaAsset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { ApprovalBadge, DraftBadge } from '~publish/components/PostStatusBadge'
import { PostTypeIcon } from '~publish/components/PostType'
import type { PostStatus } from '~publish/components/graphqlEnums'
import { format } from '~publish/helpers/temporal'
import { useTimezone } from '~publish/hooks/useTimezone'
import type { CalendarPostCard_PostData } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'

import { useTwentyFourHourTimeFormat } from '../../../hooks/useTwentyFourHourTimeFormat'

import styles from './PostMiniature.module.css'

const DEFAULT_MAX_TAGS = 3
type StatusBadgePlacement = 'row' | 'header'

interface PostMiniatureStyle extends React.CSSProperties {
  '--post-miniature-text-line-clamp': number
  '--post-miniature-grid-areas': string
}

const calculateGridTemplateAreas = ({
  renderAssets,
  renderBody,
  condensed,
  status,
  statusBadgePlacement,
}: {
  renderAssets: boolean
  renderBody: boolean
  condensed: boolean
  status: PostStatus
  statusBadgePlacement: StatusBadgePlacement
}): string => {
  /** Default */
  let templateAreas = `
    'header header'
    'body body'
  `

  /** With assets */
  if (renderAssets) {
    templateAreas = `
      'header header'
      'body assets'
    `
  }

  /** Condensed variations */
  if (condensed) {
    templateAreas = `
      'header assets'
      'body assets'
    `

    /** Only header */
    if (!renderBody && !renderAssets) {
      templateAreas = "'header header'"
    }

    /** Body, no assets */
    if (renderBody && !renderAssets) {
      templateAreas = `
        'header header'
        'body body'
      `
    }

    /** Assets, no body */
    if (!renderBody && renderAssets) {
      templateAreas = "'header assets'"
    }
  }

  /** With status */
  if (
    statusBadgePlacement === 'row' &&
    (status === 'draft' || status === 'needs_approval')
  ) {
    templateAreas = "'status status' " + templateAreas
  }
  return templateAreas
}

interface PostMiniatureProps {
  condensed?: boolean
  compact?: boolean
  post: CalendarPostCard_PostData
  className?: string
  statusBadgePlacement?: StatusBadgePlacement
  /** Draws the unread-Notes corner; the owning trigger names it. */
  hasUnreadNotes?: boolean
}

export const PostMiniature: React.FC<PostMiniatureProps> = ({
  condensed,
  compact,
  post,
  className,
  statusBadgePlacement = 'row',
  hasUnreadNotes = false,
}) => {
  const { assets, channelService, dueAt, metadata, tags } = post
  const timeZone = useTimezone()
  const is24Hour = useTwentyFourHourTimeFormat()

  const hasAssets = assets.length > 0
  const hasTags = tags.length > 0

  const lineClamp = React.useMemo(() => {
    if (hasTags) return 1
    if (hasAssets && !hasTags) return 2
    if (!hasAssets && !hasTags) return 2
    return 1
  }, [hasAssets, hasTags])

  const formattedTime = React.useMemo(() => {
    if (!dueAt) return ''
    const zonedDueAt = Temporal.Instant.from(dueAt).toZonedDateTimeISO(timeZone)

    return format(zonedDueAt, is24Hour ? 'HH:mm' : 'h:mm a')
  }, [dueAt, is24Hour, timeZone])
  const headerLabel = formattedTime || post.channel.name

  const maxVisibleTags = condensed ? 1 : DEFAULT_MAX_TAGS
  const tagsToRender = tags.slice(0, maxVisibleTags)
  const tagsRemainder = tags.slice(maxVisibleTags)

  const renderBody = hasTags || !condensed
  const renderAssets = !!hasAssets && !!assets?.[0]

  /**
   * Calculate grid areas based on the content to render and status of post
   */
  const gridTemplateAreas = React.useMemo(
    () =>
      calculateGridTemplateAreas({
        renderAssets,
        renderBody,
        condensed: !!condensed,
        status: post.status,
        statusBadgePlacement,
      }),
    [renderAssets, renderBody, post.status, condensed, statusBadgePlacement],
  )

  const isRecurringEnabled = useIsSplitEnabled('publish-recurring-posts')
  const recurrenceSummary = getRecurrenceSummary(
    post.recurrence,
    post.dueAt,
    isRecurringEnabled,
    timeZone,
  )

  const cardStyle: PostMiniatureStyle = {
    '--post-miniature-text-line-clamp': lineClamp,
    '--post-miniature-grid-areas': gridTemplateAreas,
  }

  const { schedulingType, status, notificationStatus } = post
  const notifyIcon = React.useMemo(() => {
    if (schedulingType === 'notification') {
      if (status === 'sent' && notificationStatus === 'markedAsPublished') {
        return <NotifyMePublished size="xsmall" className={styles.notifyIcon} />
      }
      return <NotifyMeIcon size="xsmall" className={styles.notifyIcon} />
    }
    return null
  }, [schedulingType, status, notificationStatus])
  const recurrenceIcon = React.useMemo(() => {
    if (status === 'scheduled' && recurrenceSummary) {
      return (
        <Tooltip content={recurrenceSummary}>
          <RepeatIcon size="xsmall" className={styles.recurringIcon} />
        </Tooltip>
      )
    }
    return null
  }, [status, recurrenceSummary])
  const statusBadge =
    post.status === 'draft' || post.status === 'needs_approval' ? (
      <div className={styles.statusBadge}>
        {post.status === 'needs_approval' && <ApprovalBadge size="small" />}
        {post.status === 'draft' && <DraftBadge size="small" />}
      </div>
    ) : null

  if (compact) {
    return (
      <div
        className={clsx(styles.compactCard, className)}
        data-status={post.status}
        data-unread-notes={hasUnreadNotes || undefined}
      >
        <span className={styles.channelIcon} data-channel={channelService}>
          <ChannelIcon type={channelService} size={12} />
        </span>
      </div>
    )
  }

  return (
    <Card
      padding="none"
      className={clsx(styles.card, className)}
      data-condensed={condensed}
      data-status={post.status}
      data-unread-notes={hasUnreadNotes || undefined}
      style={cardStyle}
    >
      {statusBadgePlacement === 'row' && statusBadge}
      <div className={styles.header}>
        <Flex
          align={formattedTime && !condensed ? 'baseline' : 'center'}
          gap="2xs"
          wrap={formattedTime ? 'wrap' : 'nowrap'}
          className={styles.headerContent}
        >
          <span className={styles.channelIcon} data-channel={channelService}>
            <ChannelIcon type={channelService} size={10} />
          </span>
          <Text
            as={dueAt ? 'time' : 'span'}
            size="sm"
            weight="medium"
            className={styles.dueAt}
            data-label={formattedTime ? 'time' : 'channel'}
            // Undated posts fall back to the channel name here, which must stay
            // verbatim; a formatted time is ours to translate.
            translate={formattedTime ? undefined : 'no'}
          >
            {headerLabel}
          </Text>
          {notifyIcon}
          {recurrenceIcon}
        </Flex>
        <div className={styles.headerTrailing}>
          <PostTypeIcon
            type={metadata?.type}
            size="small"
            className={styles.postType}
          />
          {statusBadgePlacement === 'header' && statusBadge}
        </div>
      </div>
      {renderBody && (
        <div className={styles.body}>
          {!condensed && (
            <Text className={styles.textContent} translate="no">
              {post.text}
            </Text>
          )}
          {hasTags && (
            <Flex
              gap="2xs"
              align="center"
              className={styles.tags}
              translate="no"
            >
              {tagsToRender.map((tag) => (
                <Tag key={tag.id} color={tag.color} className={styles.tag}>
                  {tag.name}
                </Tag>
              ))}
              {tagsRemainder.length > 0 && (
                <span className={styles.extraTagsText}>
                  +{tagsRemainder.length}
                </span>
              )}
            </Flex>
          )}
        </div>
      )}
      {renderAssets && (
        <PostMediaAsset asset={assets[0]} className={styles.assets} />
      )}
    </Card>
  )
}
